// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { format } from "date-fns"
import { enGB } from "date-fns/locale"
import type { ColumnDef } from "@tanstack/react-table"
import { defineMeta } from "@/lib/column-meta"
import { CycleStatusBadge } from "@/views/evolutionCycles/cycleStatusBadge"
import { dateDiffText, formatCurrency } from "@/utils"
import { Dash } from "./states"

/**
 * The column model of the My Forges table — order, labels, visibility
 * defaults and the sort accessors, per the frozen design. The reference
 * `ColumnDef<Forge>[]` for the feature (PRD §Modules).
 *
 * Each `accessorFn` returns the sort key and `null` for an absent value —
 * SmartTable's nullsLast comparator sinks it in both directions — while the
 * `cell` renders the display (which dashes a missing value: never `0`, never
 * blank).
 */

const STATUS_RANK: Record<CycleStatus, number> = {
  running: 0,
  completed: 1,
  expired: 2,
  failed: 3,
}

/** Togglable columns hidden until the user opts them in (US §9). */
export const FORGE_DEFAULT_HIDDEN = ["objective", "forge_id"] as const

export const forgeColumns: ColumnDef<Forge>[] = [
  {
    id: "name",
    header: "Forge",
    enableHiding: false,
    accessorFn: (f) => f.name.toLowerCase(),
    cell: ({ row }) => (
      <span className="font-medium text-primary transition-[text-shadow] duration-200 group-hover:[text-shadow:0_0_12px_rgba(69,240,200,0.5)]">
        {row.original.name}
      </span>
    ),
    meta: defineMeta<Forge>({ width: 1.8 }),
  },
  {
    id: "last_cycle_status",
    header: "Status",
    accessorFn: (f) =>
      f.last_cycle_status ? STATUS_RANK[f.last_cycle_status] : null,
    cell: ({ row }) =>
      row.original.last_cycle_status ? (
        <CycleStatusBadge status={row.original.last_cycle_status} />
      ) : (
        <Dash />
      ),
    meta: defineMeta<Forge>({ width: 1.3 }),
  },
  {
    id: "best_score",
    header: "Best score",
    enableHiding: false,
    accessorFn: (f) => f.best_score,
    // One neutral colour for every score: metrics are not normalized across
    // forges (accuracy ∈ [0,1] vs p95_ms in ms), so a threshold palette
    // would mislabel a good latency as a bad score.
    cell: ({ row }) =>
      row.original.best_score == null ? (
        <Dash />
      ) : (
        <span className="flex items-baseline gap-1.5">
          <span className="font-mono text-[13.5px] font-semibold text-text-primary">
            {row.original.best_score.toFixed(3)}
          </span>
          <span
            aria-hidden="true"
            className="font-mono text-[9.5px] whitespace-nowrap text-primary opacity-0 transition-opacity duration-200 group-hover:opacity-75"
          >
            → arena
          </span>
        </span>
      ),
    meta: defineMeta<Forge>({ width: 0.95 }),
  },
  {
    id: "objective",
    header: "Objective",
    accessorFn: (f) => f.objective_metric_name?.toLowerCase() ?? null,
    cell: ({ row }) =>
      row.original.objective_metric_name ? (
        <span className="font-mono text-xs">
          {row.original.objective_metric_name}
        </span>
      ) : (
        <Dash />
      ),
    meta: defineMeta<Forge>({ width: 1.15 }),
  },
  {
    id: "n_agents",
    header: "Agents",
    accessorFn: (f) => f.n_agents,
    cell: ({ row }) =>
      row.original.n_agents == null ? (
        <Dash />
      ) : (
        <span className="font-mono text-xs">{row.original.n_agents}</span>
      ),
    meta: defineMeta<Forge>({ width: 0.65, align: "right" }),
  },
  {
    id: "n_cycles",
    header: "Cycles",
    accessorFn: (f) => f.n_cycles,
    cell: ({ row }) => (
      <span
        className={
          row.original.n_cycles === 0
            ? "font-mono text-xs text-text-muted"
            : "font-mono text-xs"
        }
      >
        {row.original.n_cycles}
      </span>
    ),
    meta: defineMeta<Forge>({ width: 0.65, align: "right" }),
  },
  {
    id: "total_cost",
    header: "Cost",
    accessorFn: (f) => f.total_cost,
    cell: ({ row }) =>
      row.original.total_cost == null ? (
        <Dash />
      ) : (
        <span className="font-mono text-xs">
          {formatCurrency(row.original.total_cost)}
        </span>
      ),
    meta: defineMeta<Forge>({ width: 0.8, align: "right" }),
  },
  {
    id: "last_cycle_at",
    header: "Last cycle",
    accessorFn: (f) => (f.last_cycle_at ? Date.parse(f.last_cycle_at) : null),
    cell: ({ row }) =>
      row.original.last_cycle_at ? (
        <span className="text-text-tertiary">
          {dateDiffText(row.original.last_cycle_at)}
        </span>
      ) : (
        <Dash />
      ),
    meta: defineMeta<Forge>({ width: 1.05 }),
  },
  {
    id: "created_at",
    header: "Created",
    accessorFn: (f) => Date.parse(f.created_at),
    cell: ({ row }) => (
      <span className="font-mono text-xs text-text-tertiary">
        {format(new Date(row.original.created_at), "d MMM yyyy", {
          locale: enGB,
        })}
      </span>
    ),
    meta: defineMeta<Forge>({ width: 1.1 }),
  },
  {
    id: "forge_id",
    header: "Forge ID",
    accessorFn: (f) => f.forge_id,
    cell: ({ row }) => (
      <span className="select-text font-mono text-xs text-text-tertiary">
        {row.original.forge_id}
      </span>
    ),
    meta: defineMeta<Forge>({ width: 1.75 }),
  },
]

/**
 * The ids of the always-visible columns, derived from the column model's
 * `enableHiding: false` flags — so a crafted `?cols=` deep link cannot hide
 * them (CORR-03). Kept as a derivation, not a second hardcoded list.
 */
export const FORGE_NON_HIDEABLE = forgeColumns
  .filter((column) => column.enableHiding === false)
  .map((column) => column.id as string)
